import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, CircularProgress, Alert, Table, TableHead, TableRow, TableCell,
  TableBody, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import ConfirmDialog from '../../components/ConfirmDialog';
import { DetailRow, InvTextField, InvSelect } from '../../components/InvField';
import BatchChip from '../../components/BatchChip';
import { getDispense, getDispenseItems } from '../../api/dispensing';
import { getPrescription, getBedNurses } from '../../api/pharmacy';
import { resolveHandoverRecipient, isRecipientId } from '../../utils/handoverRecipient';
import { dispenseOrderNo } from '../../utils/dispenseOrderLink';
import { getStores } from '../../api/stockLedger';
import useItemNames from '../../hooks/useItemNames';
import { formatDate, formatCurrency } from '../../utils';
import { storeRowId } from '../../utils/storeEntity';
import {
  resolvePharmacyDispenseScope,
  runAllocateDispense,
  runDeliverDispense,
  resolveDispenseStage,
  resolveDispenseLineBatch,
  canAllocateDispense,
  canDeliverDispense,
  canReserveDispense,
  runReserveDispenseStock,
} from '../../utils/pharmacyDispenseFlow';
import { isControlledOrNarcotic } from '../../utils/controlledDrug';
import LockIcon from '@mui/icons-material/Lock';
import { IPD_COLORS } from '../../theme';
import { fadeInSx, lineTableSx, summaryHighlightSx } from './pharmacyStyles';
import { SummaryStat } from './PharmacyUi';

const STAGE_LABELS = {
  VERIFICATION: 'Verified',
  FILL: 'Reserved / Fill',
  ALLOCATE: 'Allocated',
  DELIVER: 'Delivered',
};

/**
 * API returns dispensed/reserved buckets as "0" until later stages.
 * `??` would stop on "0" and never reach requested_quantity — skip non-positive values.
 */
function resolveDispenseLineQty(item) {
  if (!item || typeof item !== 'object') return 0;
  const keys = [
    'reserved_quantity',
    'dispense_quantity',
    'requested_quantity',
    'approved_quantity',
    'issued_quantity',
    'dispensed_quantity',
    'quantity',
  ];
  for (const key of keys) {
    const n = Number(item[key]);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

export default function DispenseDetail() {
  const { id } = useParams();
  const history = useHistory();
  const { resolveItem } = useItemNames();
  const [dispense, setDispense] = useState(null);
  const [dispItems, setDispItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [doctorName, setDoctorName] = useState('');
  const [patientName, setPatientName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allocateOpen, setAllocateOpen] = useState(false);
  const [allocating, setAllocating] = useState(false);
  const [deliverOpen, setDeliverOpen] = useState(false);
  const [delivering, setDelivering] = useState(false);
  const [handoverTo, setHandoverTo] = useState('');
  // Candidate recipients for the handover field, so the pharmacist picks a
  // name instead of typing a uuid4 by hand.
  const [recipient, setRecipient] = useState({ value: '', label: '', options: [], source: '' });
  const [reserving, setReserving] = useState(false);
  const [reserveOpen, setReserveOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [d, items] = await Promise.all([
        getDispense(id),
        getDispenseItems(id).catch(() => []),
      ]);
      setDispense(d);
      setDispItems(Array.isArray(items) ? items : (items?.items || []));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getStores({ is_pharmacy_store: true })
      .then((d) => setStores(Array.isArray(d) ? d : (d?.items || [])))
      .catch(() => setStores([]));
  }, []);

  // Doctor / patient names live on the IPD prescription, not the dispense
  // record — and so does the nurse the handover field needs. The prescription
  // stamps a nurse only about half the time (empty when the bed had no active
  // assignment at order time) but always carries bed_no, so the bed's current
  // roster fills the gap and, being current, is the better default anyway.
  useEffect(() => {
    const key = dispenseOrderNo(dispense);
    if (!key) {
      setDoctorName('');
      setPatientName('');
      setRecipient({ value: '', label: '', options: [], source: '' });
      return undefined;
    }
    let alive = true;
    getPrescription(key)
      .then(async (rx) => {
        if (!alive) return;
        setDoctorName(rx?.doctor_name || '');
        setPatientName(rx?.name || rx?.patient_name || '');
        const bedNurses = await getBedNurses(rx?.bed_no);
        if (!alive) return;
        setRecipient(resolveHandoverRecipient(rx, bedNurses));
      })
      .catch(() => {
        if (!alive) return;
        setDoctorName('');
        setPatientName('');
        setRecipient({ value: '', label: '', options: [], source: '' });
      });
    return () => { alive = false; };
  }, [dispense]);

  // Seed the field each time the dialog opens, not on resolve — so reopening
  // after an edit returns to the suggestion rather than a stale typo.
  useEffect(() => {
    if (deliverOpen) setHandoverTo(recipient.value);
  }, [deliverOpen, recipient.value]);

  function resolveStoreName(storeId) {
    if (!storeId) return '—';
    const store = stores.find((s) => String(storeRowId(s)) === String(storeId));
    return store?.store_name || storeId;
  }

  function scopeForActions() {
    return resolvePharmacyDispenseScope({
      tenant_id: dispense?.tenant_id,
      hospital_id: dispense?.hospital_id,
      from_store_id: dispense?.from_store_id || dispense?.store_id,
    }, stores);
  }

  async function doReserve() {
    setReserving(true);
    try {
      const uiLines = dispItems.map((item) => ({
        item_id: item.item_id,
        dispense_qty: resolveDispenseLineQty(item),
        uom_id: item.uom_id || item.dispense_uom_id,
        batch_number: item.batch_number || resolveDispenseLineBatch(item).batchNumber || '',
        controlled_auth_id: item.controlled_auth_id || null,
        is_controlled_drug: item.is_controlled_drug,
        is_narcotic: item.is_narcotic,
        controlled: isControlledOrNarcotic(item),
      }));
      await runReserveDispenseStock({
        transactionId: id,
        uiLines,
        txnItems: dispItems,
        scope: scopeForActions(),
      });
      toast.success('Stock reserved');
      setReserveOpen(false);
      load();
    } catch (e) {
      const msg = e?.message || 'Reserve failed';
      if (e?.code === 'CONTROLLED_AUTH_PENDING' || e?.code === 'CONTROLLED_AUTH_REQUIRED') {
        toast.error('Supervisor approval pending — authorize in Controlled Queue first');
      } else {
        toast.error(msg);
      }
    } finally {
      setReserving(false);
    }
  }

  async function doAllocate() {
    setAllocating(true);
    try {
      await runAllocateDispense(id, { allocation_notes: 'Allocated to patient' }, scopeForActions());
      toast.success('Dispense allocated');
      setAllocateOpen(false);
      load();
    } catch (e) {
      toast.error(e?.message || 'Allocation failed');
    } finally {
      setAllocating(false);
    }
  }

  async function doDeliver() {
    if (!handoverTo.trim()) {
      toast.error('Select the handover recipient (nurse / ward / patient rep)');
      return;
    }
    // The dispensing service attributes receipt to this id and rejects
    // anything that isn't a UUID with an opaque 422 — catch it here so the
    // pharmacist gets a sentence they can act on.
    if (!isRecipientId(handoverTo)) {
      toast.error('Handover recipient must be a nurse ID (UUID). Pick a name from the list.');
      return;
    }
    setDelivering(true);
    try {
      await runDeliverDispense(id, { handover_to: handoverTo.trim() }, scopeForActions());
      toast.success('Medicines delivered');
      // The order book is no longer stamped from here. The deliver endpoint
      // credits delivered_quantity server-side inside the same request
      // (InventoryPharmacyDispensingDeliverView), so the write can no longer be
      // lost between two calls — which is what stranded orders in the queue.
      //
      // It also must not be stamped from here: 'Dispensed' is now DERIVED from
      // the quantity the ward accepts, so update_ipd_prescription_status/
      // rejects it outright and this call would only ever produce a misleading
      // warning toast on a delivery that actually succeeded.
      setDeliverOpen(false);
      setHandoverTo('');
      load();
    } catch (e) {
      toast.error(e?.message || 'Delivery failed');
    } finally {
      setDelivering(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pt: 10, gap: 2 }}>
        <CircularProgress sx={{ color: IPD_COLORS.primary }} />
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 14 }}>Loading dispense details…</Typography>
      </Box>
    );
  }
  if (error) return <Box sx={{ p: 4 }}><Alert severity="error" sx={{ borderRadius: '10px' }}>{error}</Alert></Box>;
  if (!dispense) return null;

  const stage = resolveDispenseStage(dispense);
  const showReserve = canReserveDispense(dispense);
  const showAllocate = canAllocateDispense(dispense);
  const showDeliver = canDeliverDispense(dispense);
  const hasControlledLines = dispItems.some((i) => isControlledOrNarcotic(i));

  const totalValue = dispItems.reduce((sum, i) => {
    const qty = resolveDispenseLineQty(i);
    const rate = Number(i.unit_rate) || 0;
    const lineTotal = Number(i.line_total);
    return sum + (Number.isFinite(lineTotal) && lineTotal > 0 ? lineTotal : qty * rate);
  }, 0);
  const totalQty = dispItems.reduce((sum, i) => sum + resolveDispenseLineQty(i), 0);

  return (
    <Box sx={fadeInSx}>
      <PageHeader
        eyebrow="Pharmacy Management › Dispenses"
        title={dispense.transaction_number}
        description={`Dispensed on ${formatDate(dispense.transaction_date)}${stage ? ` · Stage: ${STAGE_LABELS[stage] || stage}` : ''}`}
        badge={dispense.status}
        actions={
          <>
            <InvButton variant="outlined" tone="teal" size="small" onClick={() => history.goBack()}>Back</InvButton>
            {showReserve && (
              <InvButton size="small" tone="teal" onClick={() => setReserveOpen(true)}>Reserve Stock</InvButton>
            )}
            {showAllocate && (
              <InvButton size="small" tone="mint" onClick={() => setAllocateOpen(true)}>Allocate</InvButton>
            )}
            {showDeliver && (
              <InvButton size="small" onClick={() => setDeliverOpen(true)}>Deliver</InvButton>
            )}
          </>
        }
      />

      {hasControlledLines && showReserve && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
          This dispense includes controlled medicines. Supervisor must authorize all lines in
          Controlled Queue before stock can be reserved.
        </Alert>
      )}

      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
        <Grid item xs={6} sm={3}>
          <SummaryStat label="Items" value={dispItems.length} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryStat label="Total Qty" value={totalQty} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryStat label="Total Value" value={formatCurrency(totalValue)} highlight />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryStat label="Stage" value={stage ? (STAGE_LABELS[stage] || stage) : '—'} />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <SectionCard title="Dispense Details" actions={
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <StatusChip status={dispense.status} />
              {stage && <StatusChip status={stage} />}
            </Box>
          }>
            <DetailRow label="Patient">{patientName || dispense.patient_id}</DetailRow>
            <DetailRow label="Admission">{dispense.admission_id}</DetailRow>
            <DetailRow label="Doctor">{doctorName || dispense.doctor_id}</DetailRow>
            <DetailRow label="Store">{resolveStoreName(dispense.from_store_id || dispense.store_id)}</DetailRow>
            {/* <DetailRow label="Billing account">{dispense.billing_account_id}</DetailRow> */}
            <DetailRow label="Date">{formatDate(dispense.transaction_date)}</DetailRow>
            <DetailRow label="Remarks">{dispense.remarks}</DetailRow>
            {dispense.verified_at && <DetailRow label="Verified">{formatDate(dispense.verified_at)}</DetailRow>}
            {dispense.allocated_at && <DetailRow label="Allocated">{formatDate(dispense.allocated_at)}</DetailRow>}

            <Box sx={{ ...summaryHighlightSx, mt: 2, p: 2 }}>
              <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Order Total
              </Typography>
              <Typography sx={{ fontSize: 26, fontWeight: 700, color: IPD_COLORS.primary, mt: 0.5 }}>
                {formatCurrency(totalValue)}
              </Typography>
            </Box>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={8}>
          <SectionCard title="Medicines" subtitle={`${dispItems.length} line item${dispItems.length !== 1 ? 's' : ''}`}>
            <Box sx={lineTableSx}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Medicine</TableCell>
                    <TableCell>Batch</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Rate</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Auth</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dispItems.map((item, i) => {
                    const qty = resolveDispenseLineQty(item);
                    const { batchNumber, expiryDate } = resolveDispenseLineBatch(item);
                    const rate = Number(item.unit_rate) || 0;
                    const lineTotal = Number(item.line_total);
                    const total = Number.isFinite(lineTotal) && lineTotal > 0 ? lineTotal : qty * rate;
                    return (
                      <TableRow key={item.id || i}>
                        <TableCell sx={{ fontWeight: 500 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {resolveItem(item)}
                            {isControlledOrNarcotic(item) && (
                              <LockIcon sx={{ fontSize: 14, color: IPD_COLORS.warning.text }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell><BatchChip batchNumber={batchNumber} expiryDate={expiryDate} /></TableCell>
                        <TableCell align="right">{qty}</TableCell>
                        <TableCell align="right">{formatCurrency(rate)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(total)}</TableCell>
                        <TableCell>
                          {isControlledOrNarcotic(item) ? (
                            item.controlled_auth_id
                              ? <StatusChip status="AUTHORIZED" />
                              : <StatusChip status="AWAITING_AUTHORIZATION" />
                          ) : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow>
                    <TableCell colSpan={5} align="right" sx={{
                      fontWeight: 700, color: IPD_COLORS.primary,
                      borderTop: `2px solid ${IPD_COLORS.border}`, backgroundColor: IPD_COLORS.panelHeaderBg,
                    }}>
                      Total
                    </TableCell>
                    <TableCell align="right" sx={{
                      fontWeight: 700, color: IPD_COLORS.primary, fontSize: 15,
                      borderTop: `2px solid ${IPD_COLORS.border}`, backgroundColor: IPD_COLORS.panelHeaderBg,
                    }}>
                      {formatCurrency(totalValue)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </SectionCard>
        </Grid>
      </Grid>

      <ConfirmDialog
        open={reserveOpen}
        title="Reserve Stock"
        description={hasControlledLines
          ? 'Reserve stock for all lines. Controlled medicines must have supervisor authorization.'
          : 'Reserve stock for this dispense?'}
        loading={reserving}
        onConfirm={doReserve}
        onCancel={() => setReserveOpen(false)}
      />

      <ConfirmDialog
        open={allocateOpen}
        title="Allocate Dispense"
        description="Allocate reserved stock to the patient before delivery?"
        loading={allocating}
        onConfirm={doAllocate}
        onCancel={() => setAllocateOpen(false)}
      />

      <Dialog open={deliverOpen} onClose={() => !delivering && setDeliverOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Deliver Medicines</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mb: 2 }}>
            Final handover to nurse, ward staff, or patient representative.
          </Typography>
          {recipient.options.length > 0 ? (
            <InvSelect
              label="Handover to *"
              value={recipient.options.some((o) => o.value === handoverTo) ? handoverTo : ''}
              onChange={(e) => setHandoverTo(e.target.value)}
              options={recipient.options.map((o) => ({
                value: o.value,
                label: o.note ? `${o.label} · ${o.note}` : o.label,
              }))}
              helperText={
                recipient.source === 'bed'
                  ? "Suggested from the nurse currently assigned to this patient's bed — change it if someone else is collecting."
                  : 'Suggested from the nurse on the order — change it if someone else is collecting.'
              }
              fullWidth
            />
          ) : (
            <InvTextField
              label="Handover to (nurse id) *"
              value={handoverTo}
              onChange={(e) => setHandoverTo(e.target.value)}
              placeholder="Recipient UUID"
              helperText="No nurse is assigned to this patient's bed — enter the recipient's nurse ID."
              fullWidth
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <InvButton variant="outlined" tone="teal" disabled={delivering} onClick={() => setDeliverOpen(false)}>
            Cancel
          </InvButton>
          <InvButton loading={delivering} onClick={doDeliver} disabled={!handoverTo.trim()}>
            Confirm Delivery
          </InvButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
